import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getLatestArticles } from '@/mocks/articles';

export default function AboutPage() {
  const canonicalUrl = 'https://91.cc/about';
  const latestArticles = getLatestArticles(4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于 91.cc 信息导航站',
    description: '91.cc 官方平台入口与核心功能全解析，了解品牌背景、平台信誉及联系方式。',
    url: canonicalUrl,
    about: {
      '@type': 'Organization',
      name: '91.cc 信息导航站',
      description: '91.cc 第三方信息聚合平台，为用户提供全面的使用指南、最新动态和常见问题解答。',
    },
  };

  return (
    <Layout>
      <SEO
        title="关于我们 - 91.cc 官方平台信息导航"
        description="了解 91.cc 信息导航站的品牌背景、平台信誉和联系方式。我们致力于为用户提供全面、准确、及时的 91.cc 平台信息服务。"
        keywords="91.cc,关于91.cc,91.cc品牌介绍,91.cc联系方式"
        canonicalUrl={canonicalUrl}
        jsonLd={jsonLd}
      />

      <Breadcrumb
        items={[
          { label: '首页', href: '/' },
          { label: '关于我们' },
        ]}
      />

      <section className="w-full bg-background-50 py-8 md:py-12" aria-labelledby="about-heading">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h1 id="about-heading" className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
            关于我们
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mb-10 leading-relaxed">
            91.cc 信息导航站是独立的第三方信息聚合平台，致力于为 91.cc 用户提供全面、准确、及时的平台信息服务。
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            <div className="lg:col-span-2 space-y-8">
              <div id="brand">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">品牌介绍</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    91.cc 信息导航站成立于 2024 年，是一个专注于 91.cc 平台信息聚合与传播的第三方服务站点。我们与 91.cc 官方平台保持信息同步，为用户提供最新、最全的平台入口、功能指南和使用教程。
                  </p>
                  <p>
                    我们的团队由资深的互联网从业者和内容创作者组成，具有丰富的平台运营和内容策划经验。每一位团队成员都致力于精心筛选、整理和发布与 91.cc 相关的高质量信息，确保用户能够快速、准确地获取所需内容。
                  </p>
                  <p>
                    <strong>我们的使命：</strong>让每一位 91.cc 用户都能安全、便捷地访问平台，充分了解和使用平台的各项功能服务。
                  </p>
                  <p>
                    <strong>我们的愿景：</strong>成为 91.cc 用户首选的综合信息服务平台，构建全面、权威、及时的信息生态。
                  </p>
                </div>
              </div>

              <div id="reputation">
                <h2 className="text-lg md:text-xl font-bold font-heading text-foreground-900 mb-4">平台信誉</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    自上线以来，91.cc 信息导航站始终秉持"客观、准确、及时"的内容原则，赢得了广大用户的信任和好评。我们坚持：
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>信息来源于 91.cc 官方渠道，确保内容的真实性和权威性</li>
                    <li>内容定期更新维护，与平台最新动态保持同步</li>
                    <li>严格的内容审核机制，杜绝虚假和误导性信息</li>
                    <li>用户反馈快速响应，持续优化内容质量和服务体验</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  {[
                    { value: '50万+', label: '月均访问用户' },
                    { value: '100+', label: '原创文章' },
                    { value: '99.9%', label: '信息准确率' },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-background-100 rounded-lg p-5 text-center border border-background-200/60">
                      <div className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">{stat.value}</div>
                      <div className="text-xs text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div id="contact" className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-4">联系方式</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">电子邮箱</p>
                      <p className="text-foreground-800">contact@91-cc-info.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-global-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">官方网站</p>
                      <a href="https://91.cc" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600 transition-colors">
                        91.cc
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-time-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">服务时间</p>
                      <p className="text-foreground-800">周一至周五 9:00-18:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-4">最新内容</h3>
                <div className="space-y-3">
                  {latestArticles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="block group cursor-pointer"
                    >
                      <h4 className="text-sm font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {article.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">{article.publishedAt}</p>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 border border-background-200/60">
                <h3 className="text-base font-semibold font-heading text-foreground-900 mb-3">免责声明</h3>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  本站为第三方信息聚合平台，非 91.cc 官方网站。本站发布的内容仅供参考，不构成任何使用建议。用户在使用 91.cc 平台时应遵守相关法律法规和平台规定。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}